import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import MathText from "@/components/Math";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

export default function Worksheet() {
  const { chapterId } = useParams();
  const [ws, setWs] = useState(null);

  useEffect(() => {
    api.get(`/worksheet/${chapterId}`).then((r) => setWs(r.data));
  }, [chapterId]);

  if (!ws) return <div className="text-zinc-500">Loading worksheet…</div>;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex justify-between items-center mb-4 no-print">
        <Link to={`/chapters/${chapterId}`} className="text-xs text-zinc-500 hover:text-yellow-300 inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to chapter
        </Link>
        <Button
          onClick={() => window.print()}
          className="bg-sky-400 hover:bg-sky-300 text-slate-900 rounded-full font-semibold"
          data-testid="print-worksheet"
        >
          <Printer className="w-4 h-4 mr-1" /> Print worksheet
        </Button>
      </div>

      <div id="worksheet" className="bg-white text-black rounded-lg p-8 print:p-0 print:bg-white shadow-lg [&_.katex]:!text-black [&_.katex_*]:!text-black">
        {/* Header */}
        <div className="border-b-2 border-black pb-3 mb-6 flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">CBSE Maths · {ws.class_label}</div>
            <h1 className="font-display text-2xl font-bold mt-1">Chapter {ws.chapter_number}: {ws.chapter_title}</h1>
          </div>
          <div className="text-right text-xs text-neutral-500 leading-relaxed">
            <div>Name: ________________________</div>
            <div className="mt-1">Date: ______________</div>
            <div className="mt-1">Marks: ____ / {ws.questions.length}</div>
          </div>
        </div>

        <p className="text-sm text-neutral-700 mb-6"><MathText>{ws.summary}</MathText></p>

        {/* Questions */}
        <div className="mb-8">
          <h2 className="font-display text-lg font-bold mb-3">Questions</h2>
          <ol className="space-y-4 list-decimal list-inside">
            {ws.questions.map((q) => (
              <li key={q.id} className="text-sm leading-relaxed">
                <MathText>{q.q}</MathText>
                <div className="border-b border-dashed border-neutral-300 mt-3" style={{ height: "48px" }} />
              </li>
            ))}
          </ol>
        </div>

        {/* Extra practice */}
        {ws.extra_practice?.length > 0 && (
          <div className="mb-8">
            <h2 className="font-display text-lg font-bold mb-3">Extra Practice (Optional)</h2>
            <ul className="list-disc list-inside space-y-2 text-sm">
              {ws.extra_practice.map((p, i) => (
                <li key={i}><MathText>{p}</MathText></li>
              ))}
            </ul>
          </div>
        )}

        {/* Answer key (starts on new page when printed) */}
        <div className="print:break-before-page mt-8 pt-6 border-t-2 border-black">
          <h2 className="font-display text-lg font-bold mb-4">Answer Key (For Teachers)</h2>
          <ol className="space-y-3 list-decimal list-inside text-sm">
            {ws.answer_key.map((a) => (
              <li key={a.id}>
                <strong>Answer:</strong> <MathText>{a.final}</MathText>
                <div className="text-xs text-neutral-600 mt-1 pl-6">
                  <em>Working:</em> {a.solution.map((s, j) => (
                    <span key={j}>{j > 0 && " → "}<MathText>{s}</MathText></span>
                  ))}
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-10 text-[10px] text-neutral-400 text-center print:mt-6">
          Gyaan Maths · CBSE Class {ws.class_id} · Chapter {ws.chapter_number}
        </div>
      </div>
    </div>
  );
}
